import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, Building2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';

export const LoginForm: React.FC = () => {
  const { login, isLoading } = useAuth();
  const { tenantInfo, subdomain } = useTenant();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const [errors, setErrors] = useState<{ email?: string; password?: string; general?: string }>({});
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Client-side form validation
  const validate = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      newErrors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!password) {
      newErrors.password = 'Password is required.';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    try {
      await login({
        email: email.trim(),
        password,
        rememberMe,
      });
      setLoginSuccess(true);
    } catch (err: any) {
      setErrors({
        general: err.message || 'Authentication failed. Please verify your credentials.',
      });
    }
  };

  const fillTestCredentials = (testEmail: string, testPass: string) => {
    setEmail(testEmail);
    setPassword(testPass);
    setErrors({});
  };

  return (
    <div style={{ width: '100%', maxWidth: '440px', margin: '0 auto' }}>
      {/* Tenant Branding Header */}
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: 'var(--brand-gradient)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            marginBottom: '16px',
            boxShadow: 'var(--brand-glow)',
          }}
        >
          <Building2 className="w-7 h-7" />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '6px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800 }}>
            {tenantInfo ? tenantInfo.name : `${subdomain}.domain.com`}
          </h2>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Workspace:</span>
          <Badge variant="primary">{subdomain}.saasapp.com</Badge>
          {tenantInfo?.tier && (
            <Badge variant="success" style={{ textTransform: 'capitalize' }}>
              {tenantInfo.tier}
            </Badge>
          )}
        </div>
      </div>

      {/* Login Card Form */}
      <div className="glass-panel" style={{ padding: '32px' }}>
        <h3 style={{ fontSize: '1.125rem', marginBottom: '8px' }}>Sign in to your organization</h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '24px' }}>
          Enter your credentials to access your isolated business dashboard.
        </p>

        {errors.general && (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-danger-bg)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#fca5a5',
              fontSize: '0.8125rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              marginBottom: '20px',
            }}
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-danger)', marginTop: '1px' }} />
            <span>{errors.general}</span>
          </div>
        )}

        {loginSuccess && (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-success-bg)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              color: '#6ee7b7',
              fontSize: '0.8125rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '20px',
            }}
          >
            <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
            <span>Authentication successful! Loading workspace...</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <Input
            label="Work Email Address"
            type="email"
            placeholder="admin@organization.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            disabled={isLoading}
            leftIcon={<Mail className="w-4 h-4" />}
            autoComplete="email"
          />

          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            disabled={isLoading}
            leftIcon={<Lock className="w-4 h-4" />}
            autoComplete="current-password"
            rightElement={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                }}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            }
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px',
              fontSize: '0.8125rem',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: 'var(--brand-primary)' }}
              />
              Remember my session
            </label>
            <a
              href="#forgot"
              onClick={(e) => {
                e.preventDefault();
                alert('Password reset link will be sent to your administrator.');
              }}
              style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}
            >
              Forgot password?
            </a>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isLoading}
            style={{ width: '100%' }}
          >
            Sign In to {tenantInfo?.name || 'Workspace'}
          </Button>
        </form>

        {/* Quick Demo Credential Helper */}
        <div
          style={{
            marginTop: '24px',
            paddingTop: '20px',
            borderTop: '1px solid var(--border-subtle)',
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-secondary)' }}>
            🧪 Quick-Fill Demo Credentials:
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => fillTestCredentials('admin@acme.com', 'AdminPass123!')}
              style={{
                padding: '4px 8px',
                borderRadius: 'var(--radius-sm)',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
                fontSize: '0.75rem',
                cursor: 'pointer',
              }}
            >
              Acme Admin (admin@acme.com)
            </button>
            <button
              type="button"
              onClick={() => fillTestCredentials('alice@globex.com', 'GlobexPass123!')}
              style={{
                padding: '4px 8px',
                borderRadius: 'var(--radius-sm)',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
                fontSize: '0.75rem',
                cursor: 'pointer',
              }}
            >
              Globex Admin (alice@globex.com)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
